import { z } from 'zod'

/**
 * Theme manifest schema — the contract between design and the renderer.
 *
 * A theme is DATA, not code. Everything a skin needs to look different —
 * palette, labels, fonts, board chrome, sprite frames, timings — lives in
 * `themes/<id>/theme.json` and is validated here. The renderer never grows
 * a branch per game: it reads a resolved theme and draws it.
 *
 * Two rules make the pipeline safe:
 *
 *   1. Every field is OPTIONAL. A theme that only sets three colours is
 *      valid; whatever it omits falls back to the generated placeholder
 *      (see `resolve.ts`). A new game is playable before art exists.
 *
 *   2. Timings are CLAMPED, not free. Cascade duration decides spins per
 *      hour, which is an operator-facing number — a designer must not be
 *      able to halve it by editing a colour file. The ranges below are the
 *      negotiated band, and values outside it fail validation loudly.
 */

/** `#rrggbb` / `#rgb` → 0xrrggbb. Hex strings are what design tools emit. */
const Color = z
  .string()
  .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'colour must be #rgb or #rrggbb')
  .transform((s) => {
    const hex = s.slice(1)
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex
    return Number.parseInt(full, 16)
  })

/** A duration the designer may tune, but only inside an agreed band. */
const Ms = (min: number, max: number): z.ZodNumber => z.number().int().min(min).max(max)

/**
 * A file inside the theme directory.
 *
 * Absolute URLs and `..` are rejected: a theme is a self-contained folder,
 * and a manifest that reaches outside it cannot be copied, reviewed or
 * frozen as one unit. Remote art would also mean the board renders at the
 * mercy of someone else's uptime.
 */
const RelPath = z
  .string()
  .min(1)
  .refine((p) => !p.startsWith('/') && !p.includes('..') && !/^[a-z]+:/i.test(p), {
    message: 'path must be relative to the theme directory, without ".."',
  })

const SymbolStyleSchema = z.object({
  /** Tile fill. Ignored when a sprite frame is present and loaded. */
  fill: Color.optional(),
  /** Label colour. */
  text: Color.optional(),
  /** Text on the tile. Full word — abbreviations do not read at speed. */
  label: z.string().max(12).optional(),
  /** Label size. A property of the symbol: "DRAGON" and "10" cannot match. */
  fontSize: z.number().min(8).max(72).optional(),
  /** Frame name inside the atlas. Missing frame ⇒ coloured tile fallback. */
  frame: z.string().min(1).optional(),
  /**
   * A loose image file, relative to the theme directory.
   *
   * The simple path: drop `art/h1.png` next to the manifest and point at
   * it. No build step, works the moment the file is saved. An atlas is the
   * production answer (one draw call instead of one per symbol), but a
   * theme should not need a toolchain before it can show a picture.
   *
   * `frame` wins when both are set — the atlas is the built artefact.
   */
  image: RelPath.optional(),
  /**
   * Per-symbol backdrop, drawn UNDER `image` and stretched to the whole
   * cell.
   *
   * Two files instead of one baked picture, because the two layers have
   * different jobs: the backdrop carries the tile shape, the rounding and
   * the symbol's own colour, while the character keeps real transparency
   * and is fitted with `contain` so it never distorts. Baking them
   * together would mean re-exporting every character to restyle a frame.
   *
   * Missing or unloadable ⇒ the flat `fill` tile, exactly as before.
   */
  backgroundImage: RelPath.optional(),
  /**
   * Size relative to the other symbols, multiplying the fitted box.
   *
   * The fit normalises every symbol to the same box, so a bonus symbol
   * that must read as bigger cannot get there by being drawn larger —
   * only by carrying less transparent margin than its neighbours, which
   * hides an intentional size decision inside a PNG's padding. This makes
   * it explicit: 1.16 says "16% bigger than the rest" in the manifest,
   * where it can be read and changed without re-exporting art.
   *
   * Bounded because the fit exists to keep the grid even: past these
   * limits a symbol either collides with its neighbours or stops reading.
   */
  scale: z.number().min(0.5).max(1.5).optional(),
  /**
   * Flipbook animation: the symbol cycles through these images on the
   * board. Needed because symbol art is rasterised into a texture — SMIL
   * inside the SVG never plays there (unlike the mascot's <img>).
   *
   * Frames are complete images, not deltas; frame 0 is what the symbol
   * looks like at rest, so a theme usually lists its `image` first. The
   * count is capped: each frame is a full texture in GPU memory.
   */
  animation: z
    .object({
      // 48 covers a full-second loop at 30 fps, which is where a flame
      // stops reading as a stutter and starts reading as fire. Each frame
      // is still a full texture in GPU memory, so the cap stays.
      images: z.array(RelPath).min(2).max(48),
      /** Frame rate. Flames read at 8; 30 is a smooth generated flipbook. */
      fps: z.number().min(2).max(60).optional(),
    })
    .optional(),
})

const BannerStyleSchema = z.object({
  fill: Color.optional(),
  text: Color.optional(),
  /**
   * Stretchable plaque behind the text, drawn instead of the rounded
   * rectangle. Its ends are fixed and only the middle stretches, so the
   * artwork must be built with a horizontally uniform middle section.
   */
  image: RelPath.optional(),
  /** Width of one fixed end cap, in the image's own pixels. */
  cap: z.number().int().min(1).max(4096).optional(),
  /**
   * The flat field's vertical band, in the image's own pixels. The text
   * centres on this band rather than on the plaque: the rails above and
   * below it are rarely the same thickness, and centring on the whole
   * plaque then hangs the line visibly high or low.
   */
  fieldTop: z.number().int().min(0).max(4096).optional(),
  fieldBottom: z.number().int().min(0).max(4096).optional(),
  /** Offset copy of the text under the line. Omit for no shadow. */
  shadow: Color.optional(),
})

/**
 * Timing bands.
 *
 * Defaults live in `resolve.ts` next to the renderer that consumes them;
 * this file only fixes what a theme is ALLOWED to ask for.
 */
const TumbleTimingSchema = z.object({
  spinAnticipationMs: Ms(0, 200).optional(),
  spinExitMs: Ms(0, 700).optional(),
  spinExitStaggerMs: Ms(0, 120).optional(),
  spinExitRowStaggerMs: Ms(0, 90).optional(),
  emptyHoldMs: Ms(0, 200).optional(),
  dropMs: Ms(200, 700).optional(),
  dropStaggerMs: Ms(0, 120).optional(),
  dropRowStaggerMs: Ms(0, 90).optional(),
  landingMs: Ms(0, 250).optional(),
  cascadeDropMs: Ms(150, 500).optional(),
  cascadeColumnStaggerMs: Ms(0, 60).optional(),
  cascadeRowStaggerMs: Ms(0, 60).optional(),
  cascadeLandingMs: Ms(0, 180).optional(),
  highlightMs: Ms(250, 900).optional(),
  explodeMs: Ms(120, 500).optional(),
  settleMs: Ms(150, 600).optional(),
  bombMs: Ms(400, 1800).optional(),
  bombEntranceMs: Ms(120, 900).optional(),
  scatterMs: Ms(300, 1500).optional(),
  betweenSpinsMs: Ms(200, 1200).optional(),
  freeSpinsIntroMs: Ms(500, 2500).optional(),
})

const WaysTimingSchema = z.object({
  reelStartStagger: Ms(0, 250).optional(),
  minSpinDuration: Ms(400, 2000).optional(),
  reelStopStagger: Ms(0, 400).optional(),
  betweenFreeSpins: Ms(300, 2000).optional(),
  settleMs: Ms(100, 500).optional(),
  /** Reel scroll speed, px/s. Not a duration, but the same reasoning. */
  scrollSpeed: z.number().int().min(800).max(6000).optional(),
})

/** Lines: how long each winning line stays lit while the wins are cycled. */
const LinesTimingSchema = z.object({
  lineShowMs: Ms(300, 2000).optional(),
  betweenLinesMs: Ms(0, 600).optional(),
  /** All winning lines together, after the cycle. */
  allLinesMs: Ms(300, 3000).optional(),
})

export const ThemeSchema = z.object({
  /** Theme id. Must equal the directory name — the loader builds URLs from it. */
  id: z.string().regex(/^[a-z0-9-]+$/, 'id: lowercase letters, digits and dashes only'),
  name: z.string().min(1),
  version: z.string().min(1),
  /**
   * Games this theme may skin. One math release can carry several skins,
   * and one skin can cover several games — that is the point of the split.
   */
  games: z.array(z.string().min(1)).min(1),

  /** HTML chrome above the board. Kept in the theme so branded title art
   * never creates a game-id branch in the React shell. */
  header: z
    .object({
      /** Complete title treatment; accessible text is still supplied by the client. */
      logoImage: RelPath.optional(),
    })
    .optional(),

  /** Drawn titles for the win celebration, one per tier. Same idea as the
   * header logo: a theme that ships lettering gets it, a theme that does not
   * keeps the live text, and neither case puts a game id in the React shell.
   * Accessible text still comes from the tier, so a missing file costs the
   * look and nothing else. */
  winTitles: z
    .object({
      big: RelPath.optional(),
      mega: RelPath.optional(),
      epic: RelPath.optional(),
    })
    .optional(),

  /** Gems that rain behind the win celebration. Absent means no rain. */
  winCrystals: z
    .object({
      red: RelPath.optional(),
      blue: RelPath.optional(),
    })
    .optional(),

  /** Plaque behind the celebration's confirm button. Absent keeps the flat one. */
  winButton: RelPath.optional(),

  board: z
    .object({
      /** Behind the grid. Null keeps the page background. */
      background: Color.nullable().optional(),
      /** Image behind the grid, scaled to cover the board. */
      backgroundImage: RelPath.optional(),
      /**
       * Overlay drawn ON TOP of the cells: the portal, the cabinet, the
       * carved frame. It must be transparent where the symbols are — this
       * is art, and nothing clips it to the grid for you.
       */
      frameImage: RelPath.optional(),
      /**
       * Where the grid sits inside `frameImage`, as a fraction of the art's
       * own width and height, measured per side.
       *
       * Frame art is a building, not a border: a gate has thick columns and
       * a deep roof, and those proportions are the drawing's whole point.
       * Forcing such art into a band computed from the board is what makes
       * it look stretched and sliced. So the art keeps its proportions and
       * declares where its opening is; the renderer scales it uniformly
       * until that opening covers the board and centres it there.
       *
       * Four sides rather than two, because an opening is rarely centred —
       * a roof is deeper than the sill it stands on.
       *
       * Uniform scale means the opening usually has slack on one axis: the
       * board sits inside it with a little air, which is what a frame looks
       * like anyway. Each side is capped below 0.45 so the four can never
       * add up to no opening at all.
       */
      frameInset: z
        .object({
          left: z.number().min(0).max(0.45),
          right: z.number().min(0).max(0.45),
          top: z.number().min(0).max(0.45),
          bottom: z.number().min(0).max(0.45),
        })
        .refine((i) => i.left + i.right < 0.95 && i.top + i.bottom < 0.95, {
          message: 'frameInset leaves no opening: each axis must sum below 0.95',
        })
        .optional(),
      /**
       * Visible inner edge of a surround frame. When it is slightly outside
       * `frameInset`, the space between both rectangles becomes a deliberate
       * liner/gutter around the tile grid instead of the tiles touching art.
       */
      frameOpeningInset: z
        .object({
          left: z.number().min(0).max(0.45),
          right: z.number().min(0).max(0.45),
          top: z.number().min(0).max(0.45),
          bottom: z.number().min(0).max(0.45),
        })
        .refine((i) => i.left + i.right < 0.95 && i.top + i.bottom < 0.95, {
          message: 'frameOpeningInset leaves no opening',
        })
        .optional(),
      /**
       * Quiet margin between the grid and the edge of the board canvas, px.
       *
       * Without it the canvas ends exactly where the outer row of cells
       * does, so a symbol dropping in is sliced flat against that edge —
       * it appears mid-air instead of sliding out from beneath the frame.
       * Padding grows the canvas past the grid on every side, giving the
       * fall somewhere to happen: the tile is already drawn while it is
       * still under the frame art, and the frame is what hides it.
       *
       * Measured in the same board units as `cellSize`, so a theme states
       * it once and it holds at every screen scale.
       */
      gridPadding: z.number().min(0).max(64).optional(),
      /** Tile corner radius, px. Ignored for symbols drawn as art. */
      tileRadius: z.number().min(0).max(64).optional(),
      /** Overrides the mechanic's default gap between cells, px. */
      gap: z.number().int().min(0).max(40).optional(),
      /**
       * Overrides the mechanic's default cell size, px on screen (art is
       * authored @2x). A theme that sets it opts out of the shared board
       * cap — the canvas grows, and CSS scales it down on small viewports.
       * For themes with detailed art that needs a bigger cell.
       */
      cellSize: z.number().int().min(48).max(160).optional(),
      /**
       * Free space around a symbol inside its cell, as a fraction of the
       * cell. Art usually needs air; a tile does not.
       */
      symbolPadding: z.number().min(0).max(0.4).optional(),
      /**
       * How symbol art fills its cell.
       *
       * `contain` (default) keeps the aspect ratio — a non-square symbol
       * stays undistorted. `stretch` is the old behaviour and is only
       * right for art drawn square on purpose.
       */
      symbolFit: z.enum(['contain', 'stretch']).optional(),
    })
    .optional(),

  /**
   * The area around the board — what turns a grid into a game screen.
   *
   * This part is CSS, not WebGL: it is the page behind the canvas, and
   * putting it in the renderer would mean a full-screen backdrop costing
   * GPU fill rate on every frame for something that never moves.
   */
  stage: z
    .object({
      /** Page backdrop behind the whole game. */
      background: Color.optional(),
      /** Full-screen image behind the game. Covers, centred. */
      backgroundImage: RelPath.optional(),
      /**
       * Mascot art shown beside the board. Rendered as a plain <img>, so
       * an SVG with SMIL animation inside gives a living character with
       * zero JS. Purely decorative: hidden on narrow screens and never
       * intercepts clicks.
       */
      mascotImage: RelPath.optional(),
      /** Same idea, mirrored: decorative scenery on the LEFT of the board. */
      decorImage: RelPath.optional(),
      /**
       * Small floating creature shown on BOTH sides of the board, above the
       * mascot/decor. One file: the client renders it twice and mirrors the
       * right copy in CSS.
       */
      ghostImage: RelPath.optional(),
      /**
       * Keep the default panel around the board (gradient, border, shadow).
       * A theme with its own frame art sets this to false — otherwise the
       * stock cabinet shows through around the custom one.
       */
      panel: z.boolean().optional(),
      /**
       * Restyle the stock panel instead of replacing it: gradient stops for
       * the border ring and, optionally, for the panel fill behind the
       * canvas. Cheaper than frame art when all a theme wants is its own
       * colours around the board.
       */
      panelFrame: z
        .object({
          /** Border gradient stops, top to bottom. */
          border: z.array(Color).min(2).max(4),
          /** Panel fill gradient stops, top to bottom. */
          background: z.array(Color).min(2).max(4).optional(),
        })
        .optional(),
      /**
       * Cloud sprites for the `cloud` emitters, one PNG per look. When the
       * list is empty the client bakes procedural cumulus textures instead
       * — this field exists so an artist can replace them with drawn ones
       * by dropping files, no code involved.
       */
      cloudImages: z.array(RelPath).max(8).optional(),
      /**
       * Which big-win celebration the overlay plays.
       *
       * `flames` is the original: a wall of fire rushing in from the sides,
       * drawn for a hot-slot cabinet. `sunburst` is light instead of fire —
       * a golden corona radiating out of the amount itself.
       *
       * A theme decision rather than a component one, because the overlay
       * is shared by every game and the fire belongs to exactly one of
       * them. Defaults to `flames` so no existing skin changes.
       */
      celebration: z.enum(['flames', 'sunburst']).optional(),
      /**
       * Live particle sources pinned to the backdrop art: falling water in
       * a painted waterfall, mist at its foot.
       *
       * Coordinates are FRACTIONS OF THE BACKDROP IMAGE, not of the
       * viewport — the backdrop is `background-size: cover`, so on a wide
       * screen a third of the picture is cropped away and any viewport
       * coordinate would slide off the waterfall it was aimed at. The
       * client applies the same cover transform to place them.
       */
      emitters: z
        .array(
          z.object({
            /**
             * `waterfall` — a falling sheet: droplets from a slot of width
             * `width`, dying `height` below the source.
             * `mist` — haze gathering where that fall lands.
             * `cloud` — puffy clouds drifting across the scene.
             * `glow` — a breathing aura with a few rising motes, for a gem
             * or lamp painted into the art. Its `width`/`height` are the
             * PAINTED OBJECT's own bounding box, not the halo's: the halo
             * is derived from it, so a glow can be re-aimed by tracing the
             * gem in the picture and nothing else.
             */
            kind: z.enum(['waterfall', 'mist', 'cloud', 'glow']),
            /** Centre of the source, 0..1 across the image. */
            x: z.number().min(0).max(1),
            /**
             * 0..1 down the image. The TOP of the fall for `waterfall` and
             * `mist` — that is where the water leaves the lip. The CENTRE
             * for `cloud` and `glow`, which have no top to speak of.
             */
            y: z.number().min(0).max(1),
            /**
             * Fraction of the image width: the emitting slot for a
             * waterfall, the cloud's own size, the painted gem's width for
             * a glow.
             */
            width: z.number().min(0).max(1),
            /**
             * Fraction of the image height: how far the water falls, how
             * far a cloud may stray from its band, the painted gem's height
             * for a glow.
             */
            height: z.number().min(0).max(1),
            /**
             * Sideways drift from the top of the fall to its bottom, as a
             * signed fraction of the image width.
             *
             * A painted waterfall is almost never a straight column — it
             * leaves the lip with some sideways speed and gravity bends it
             * into an arc. A fountain jet is nothing but that arc. Without
             * this the particles fall as a ruler-straight band and drift
             * off the water halfway down.
             *
             * For `cloud` this is the drift SPEED instead: image widths per
             * second, and its sign picks the edge they enter from.
             */
            curve: z.number().min(-0.5).max(0.5).optional(),
            /**
             * `cloud` only: how much of the image width the clouds traverse,
             * centred on `x`, before dissolving. Defaults to edge-to-edge.
             *
             * Needed because the layer sits in FRONT of the whole painting:
             * a cloud crossing a foreground building breaks the depth the
             * backdrop establishes. A corridor keeps them in the open sky,
             * and the fade-in/out of their lifetime hides both ends.
             */
            span: z.number().min(0.05).max(1).optional(),
            /** Tint. Defaults to a pale blue-white that suits most water. */
            color: Color.optional(),
            /**
             * Particles per second. The client scales this with the
             * emitter's on-screen size, so one number works at every
             * viewport — it is density, not a raw budget.
             */
            rate: z.number().min(0).max(400).optional(),
            /** Overall opacity multiplier, for a distant, hazier source. */
            alpha: z.number().min(0).max(1).optional(),
            /**
             * `glow` only: seconds for one full breath of the halo.
             *
             * The pulse is deterministic, not an accident of how the random
             * spawns happen to overlap — an overlap-driven halo flickers at
             * the spawn rate, which reads as a failing lamp rather than as a
             * gem holding its light. Long periods (3–6 s) are what make a
             * stone look enchanted instead of electrical.
             */
            period: z.number().min(0.5).max(12).optional(),
          }),
        )
        .max(24)
        .optional(),
    })
    .optional(),

  /**
   * Sound, like every other asset: a file inside the theme directory.
   *
   * Only the background loop lives here for now. It is optional in the
   * same way art is — a theme without it plays silently and the mute
   * button does not render, rather than sitting there doing nothing.
   */
  audio: z
    .object({
      /** Looping background track. Spliced sample-accurately by the client. */
      music: RelPath.optional(),
      /**
       * A second loop for free spins — the same scene, livelier. The client
       * crossfades between the two; without this field the base loop simply
       * keeps playing, so it is optional like everything else.
       */
      musicFree: RelPath.optional(),
      /**
       * Playback level, 0–1. Capped below 1 on purpose: background music
       * competes with the win sounds that carry the actual information,
       * and a theme must not be able to drown them.
       */
      musicVolume: z.number().min(0).max(0.8).optional(),
      /**
       * One-shot effects, keyed by the event they mark: `spin-start`,
       * `reel-spin` (looped while the reels scroll), `reel-stop-1..3`,
       * `win-small`, `win-big`, `wild-hit`,
       * `gamble-flip`, `gamble-win`, `gamble-lose`, `button-click`.
       *
       * An open record rather than a fixed shape, for the same reason
       * every other theme field is optional: a skin ships the sounds it
       * has, and an event with no file is simply silent. Names the client
       * does not know are ignored, so a theme can carry sounds for a
       * mechanic it does not currently skin.
       */
      sfx: z.record(RelPath).optional(),
      /** Master level for the effects, 0–1. They carry information; default is loud. */
      sfxVolume: z.number().min(0).max(1).optional(),
    })
    .optional(),

  font: z
    .object({
      family: z.string().min(1).optional(),
      weight: z.string().min(1).optional(),
      letterSpacing: z.number().min(-2).max(6).optional(),
    })
    .optional(),

  /**
   * Sprite atlas, relative to the theme directory. Built by `theme:build`
   * from `art/`; absent atlas is not an error — the theme then renders as
   * coloured tiles, which is exactly the placeholder we have today.
   */
  atlas: z
    .object({
      url: RelPath,
      /** Frame for the whole board background, if the atlas carries one. */
      backgroundFrame: z.string().min(1).optional(),
    })
    .optional(),

  /** Keyed by symbol `code` from the game config (S, H1…L5). */
  symbols: z.record(SymbolStyleSchema).optional(),

  /** Multiplier bomb — one tile for every value, number drawn on top. */
  bomb: SymbolStyleSchema.optional(),

  banners: z
    .object({
      free: BannerStyleSchema.optional(),
      win: BannerStyleSchema.optional(),
      bomb: BannerStyleSchema.optional(),
    })
    .optional(),

  timings: z
    .object({
      tumble: TumbleTimingSchema.optional(),
      ways: WaysTimingSchema.optional(),
      lines: LinesTimingSchema.optional(),
    })
    .optional(),
})

/** Parsed manifest: colours are already numbers, nothing is resolved yet. */
export type Theme = z.infer<typeof ThemeSchema>
export type ThemeSymbolStyle = z.infer<typeof SymbolStyleSchema>
